import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export function useBatch(batchId, onFinished) {
  const { setCredits, refreshCredits } = useAuth();
  const [batch, setBatch] = useState(null);
  const [tick, setTick] = useState(0);
  const cb = useRef(onFinished);
  cb.current = onFinished;
  useEffect(() => {
    if (!batchId) { setBatch(null); return undefined; }
    let alive = true;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/batches/${batchId}`);
        if (!alive) return;
        setBatch(data);
        if (data.finished) {
          refreshCredits();
          if (data.summary?.notice) toast.warning(data.summary.notice, { duration: 12000 });
          cb.current?.(data);
          return;
        }
      } catch (e) {
        if (alive) setBatch({ error: errorMessage(e), files: [] });
        return;
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => { alive = false; clearTimeout(timer); };
  }, [batchId, tick, refreshCredits]);
  const retry = useCallback(async (extractionId) => {
    try {
      const { data } = await api.post(`/extractions/${extractionId}/retry`);
      if (data.balance !== undefined) setCredits(data.balance);
      setBatch((b) => b && { ...b, finished: false, files: b.files.map((f) => (f.extraction_id === extractionId ? { ...f, status: "pending", error: null } : f)) });
      setTick((t) => t + 1);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  }, [setCredits]);
  return { batch, retry };
}

export async function startBatch(files, quoteId) {
  const fd = new FormData();
  files.forEach((f) => fd.append("files", f));
  if (quoteId) fd.append("quote_id", quoteId);
  const { data } = await api.post("/batches", fd);
  return data;
}
